import { useEffect, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { executeQuery } from 'common/gql';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type {
  ContactFormFieldSlice,
  ContactFormResponse,
  ContactFormVariables,
  Reason,
} from '../components/types';
import contactFormQuery from '../gql/query/contactForm.gql';
import { getReasonFromReasonList, reasonContainsSubreasons } from '../utils/reasons';
import { clearStorage, getReason } from '../utils/storage';
import { reasonSelectionTrackingEvent } from '../utils/tracking';

interface UseSelectContactFormComponentParams {
  isRedirected: boolean;
  reasonFromUrl: Nullable<string>;
  reasons: Reason[];
}

const getInitialSelectedReason = ({
  isRedirected,
  reasons,
  reasonFromUrl,
}: UseSelectContactFormComponentParams) => {
  if (isRedirected) {
    const savedReason = getReason();
    if (savedReason && !!getReasonFromReasonList(reasons, savedReason)) {
      return savedReason;
    }
  }

  if (reasonFromUrl && !!getReasonFromReasonList(reasons, reasonFromUrl)) {
    return reasonFromUrl;
  }

  return null;
};

export const useSelectContactFormComponent = ({
  isRedirected,
  reasonFromUrl,
  reasons,
}: UseSelectContactFormComponentParams) => {
  const { t } = useLocalization();
  const locale = useGQLLocale();
  const [selectedReason, setSelectedReason] = useState(() =>
    getInitialSelectedReason({ isRedirected, reasons, reasonFromUrl })
  );
  const initialReason = useRef(selectedReason);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [slices, setSlices] = useState<ContactFormFieldSlice[]>([]);

  const fetchFormType = async () => {
    if (!selectedReason) return;

    setHasError(false);
    setIsLoading(true);
    try {
      const { contactForm } = await executeQuery<ContactFormResponse, ContactFormVariables>({
        query: contactFormQuery,
        variables: { locale, documentId: selectedReason },
      });
      if (contactForm !== null) {
        setSlices(contactForm.slices);
      } else {
        setHasError(true);
      }

      reasonSelectionTrackingEvent(selectedReason, t('h24_contact_form_select_topic'));
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slices.length > 0) {
      setSlices([]);
    }
    const reason = getReasonFromReasonList(reasons, selectedReason);

    setIsDisabled(!(selectedReason && !reasonContainsSubreasons(reason)));
  }, [selectedReason]);

  useEffect(() => {
    if (initialReason.current) {
      const reason = getReasonFromReasonList(reasons, initialReason.current);
      if (!reasonContainsSubreasons(reason)) {
        fetchFormType();
      }
    }
    if (!isRedirected) {
      clearStorage();
    }
  }, []);

  return {
    slices,
    setSlices,
    selectedReason,
    hasError,
    isDisabled,
    isLoading,
    fetchFormType,
    initialReason: initialReason.current,
    setSelectedReason,
  };
};
